import * as React from 'react';
import DiDataGrid from './DiDataGrid';

// Helper function to categorize filters
const categorizeFilters = (filters) => {
  const categories = {
    profile: ['value_score', 'moat_score', 'debt_score', 'management_score', 'sandp_500', 'sandp_400', 'russell_3000', 'nasdaq_100', 'earnings_announcement', 'dividend_yield_percent', 'market_cap', 'pe_ratio', 'ps_ratio', 'pb_ratio', 'eps', 'gross_margin', 'operating_margin', 'net_income_margin', 'stock_exchange_symbol', 'country'],
    valuation: ['price', 'payback_time', 'peg_ratio', 'bvps', 'average_volume', 'close_to_year_high', 'close_to_year_low', 'mos_decision'],
    growth: ['one_year_revenue_growth_rate_cagr', 'three_year_revenue_growth_rate_cagr', 'five_year_revenue_growth_rate_cagr', 'ten_year_revenue_growth_rate_cagr', 'one_year_net_income_growth_rate_cagr', 'three_year_net_income_growth_rate_cagr', 'five_year_net_income_growth_rate_cagr', 'ten_year_net_income_growth_rate_cagr', 'one_year_operating_cash_flow_growth_rate_cagr', 'three_year_operating_cash_flow_growth_rate_cagr', 'five_year_operating_cash_flow_growth_rate_cagr', 'ten_year_operating_cash_flow_growth_rate_cagr', 'one_year_equity_growth_rate_cagr', 'three_year_equity_growth_rate_cagr', 'five_year_equity_growth_rate_cagr', 'ten_year_equity_growth_rate_cagr'],
    financial: ['current_ratio', 'debt_to_equity', 'altman_z_score', 'piotroski_score', 'payout_ratio', 'revenue', 'net_income', 'roe', 'roic', 'fcf']
  };

  return filters.reduce((acc, filter) => {
    for (const [category, metrics] of Object.entries(categories)) {
      if (metrics.includes(filter.metric)) {
        if (!acc[category]) acc[category] = [];
        acc[category].push(filter);
        return acc;
      }
    }
    if (!acc.other) acc.other = [];
    acc.other.push(filter);
    return acc;
  }, {});
};

// Add these helper functions at the top of the file, after the imports
const translateCategory = (category: string): string => {
  const translations: { [key: string]: string } = {
    profile: 'Company Profile',
    valuation: 'Valuation Metrics',
    growth: 'Growth Rates',
    financial: 'Financial Metrics',
    other: 'Other Metrics'
  };
  return translations[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

const translateMetric = (metric: string): string => {
  return metric
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/Sandp/g, 'S&P')
    .replace(/Eps/g, 'EPS')
    .replace(/Pe/g, 'P/E')
    .replace(/Ps/g, 'P/S')
    .replace(/Pb/g, 'P/B')
    .replace(/Roe/g, 'ROE')
    .replace(/Roic/g, 'ROIC')
    .replace(/Fcf/g, 'FCF')
    .replace(/Cagr/g, 'CAGR');
};

const translateOperator = (operator: string): string => {
  const translations: { [key: string]: string } = {
    less_than: 'less than',
    greater_than: 'greater than',
    equal_to: 'equal to',
    not_equal_to: 'not equal to',
    less_than_or_equal_to: 'less than or equal to',
    greater_than_or_equal_to: 'greater than or equal to',
    between: 'between',
    in: 'in'
  };
  return translations[operator] || operator.replace(/_/g, ' ');
};

function StockScreen({stock_screen} :any) {
  const categorizedFilters = categorizeFilters(stock_screen.filters);

  const formatFilterValue = (operator: string, value: any): string => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value.toString();
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"></h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item"><a href="/profile">Profile</a></li>
            <li className="breadcrumb-item"><a href="/screener">Stock Screener</a></li>
            <li className="breadcrumb-item active" aria-current="page">{stock_screen.name}</li>
          </ol>
        </nav>
      </div>
      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{stock_screen.name}</h4>
              <div className="filters-summary">
                <h5>Applied Filters:</h5>
                {Object.entries(categorizedFilters).map(([category, filters]) => (
                  <div key={category} className="filter-category">
                    <h6>{translateCategory(category)}</h6>
                    <ul>
                      {(filters as any[]).map((filter, index) => (
                        <li key={index}>
                          {translateMetric(filter.metric)}: {translateOperator(filter.operator)} {formatFilterValue(filter.operator, filter.value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="table-container">
                <DiDataGrid
                  data={stock_screen.data}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .filters-summary {
          margin-bottom: 20px;
          padding: 15px;
          background-color: #f8f9fa;
          border-radius: 4px;
        }
        .filter-category {
          margin-bottom: 15px;
        }
        .filter-category h6 {
          margin-bottom: 5px;
          font-weight: bold;
        }
        .filter-category ul {
          list-style-type: none;
          padding-left: 0;
        }
        .filter-category li {
          margin-bottom: 5px;
        }
        .table-container {
          display: flex;
          flex-direction: column;
        }
        .table-container > div {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .table-container .MuiDataGrid-root {
          flex: 1;
          min-height: 400px;
          max-height: calc(100vh - 500px);
        }
        .table-container .MuiDataGrid-footerContainer {
          position: static;
        }
      `}</style>
    </div>
  );
}

export default StockScreen;
